import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import "./Onboarding.scss";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OnboardingNavCircles } from "../OnboardingNavCircles/OnboardingNavCircles";
import OwButton from "../../shared/OwButton/OwButton";
import ISuppliersCompleteOnboardingDTO from "../../../models/DTO/ISuppliersCompleteOnboardingDTO";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import AddressBlock from "../../shared/AddressBlock/AddressBlock";
import IAddress from "../../../models/IAddress";
import orderwerksLogoDarkMode from "../../../assets/images/orderwerks/orderwerks-logo-grey.svg";
import VendorNameOverrideEnum from "../../../models/VendorNameOverrideEnum";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { OnboardingThemePreview } from "../OnboardingThemePreview/OnboardingThemePreview";
import OwToggle from "../../shared/OwToggle/OwToggle";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import WhatsThis from "../../shared/WhatsThis/WhatsThis";
import IOnboardingCompanyDetail from "../../../models/IOnboardingCompanyDetail";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnSuppliersCompleteOnboardingDTO from "../../../models/DTO/IOnSuppliersCompleteOnboardingDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import ILoadUserDTO from "../../../models/DTO/ILoadUserDTO";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import * as _ from "lodash-es";
import IUpdateSupplierDTO from "../../../models/DTO/IUpdateSupplierDTO";
import IUpdateSupplierSettingsDTO from "../../../models/DTO/IUpdateSupplierSettingsDTO";
import UIService from "../../../services/UIService";
import { Socket } from "socket.io-client";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

export const Onboarding: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [currentSlide, setCurrentSlide] = useState(0);

    const [isSaving, setIsSaving] = useState(false);

    const [companyDetails, setCompanyDetails] = useState<IOnboardingCompanyDetail>({
        companyEmail: "",
        companyName: "",
        shipTo: {} as IAddress,
        billTo: {} as IAddress,
        theme: "DARK",
        accountNameDesignation: VendorNameOverrideEnum.NULL,
        enablePhotoView: false,
        enableShipments: false,
        enableInventory: false,
        enablePickListManagement: false,
        createDemoData: false,
    });

    useEffect(() => {
        if (hasLocalStorageItems()) {
            getLocalStorage();
        } else {
            const companyDetailObj: IOnboardingCompanyDetail = {
                companyName: supplierModel.name,
                companyEmail: supplierModel.email,
                shipTo: supplierModel.Ship_To,
                billTo: supplierModel.Bill_To,
                theme: "DARK",
                accountNameDesignation: supplierModel.vendor_name_override,
                enablePhotoView: false,
                enableShipments: false,
                enableInventory: false,
                enablePickListManagement: false,
                createDemoData: false,
            };

            setCompanyDetails(companyDetailObj);
        }

        props.socket.on(EndpointsEnum.SuppliersOnCompleteOnboarding, onCompleteOnboarding);
        props.socket.on(EndpointsEnum.AppOnLoadUser, onLoadUser);
        props.socket.on(EndpointsEnum.Notify, notify);

        return () => {
            props.socket.removeListener(EndpointsEnum.SuppliersOnCompleteOnboarding, onCompleteOnboarding);
            props.socket.removeListener(EndpointsEnum.AppOnLoadUser, onLoadUser);
            props.socket.removeListener(EndpointsEnum.Notify, notify);
        };
    }, []);

    useEffect(() => {
        updateLocalStorage();
    }, [companyDetails]);

    useEffect(() => {
        // const slide = searchParams.get('slide');

        const values = queryString.parse(location.search);

        if (values.slide) {
            if (values.slide !== "0") {
                setCurrentSlide(Number(values.slide));
            }
        }
    }, [location.search]);

    const notify = () => {
        setIsSaving(false);
    };

    const updateLocalStorage = () => {
        localStorage.setItem("onboarding", JSON.stringify(companyDetails));
    };

    const getLocalStorage = () => {
        const localStorageCompanyDetails = localStorage.getItem("onboarding");

        if (localStorageCompanyDetails) {
            const companyDetailsHydrated = JSON.parse(localStorageCompanyDetails);

            setCompanyDetails(companyDetailsHydrated);
        }
    };

    const hasLocalStorageItems = () => {
        const localStorageCompanyDetails = localStorage.getItem("onboarding");

        return !!localStorageCompanyDetails;
    };

    useEffect(() => {
        const values = queryString.parse(location.search);

        if (!values.slide && currentSlide > 0) {
            navigate(`${location.pathname}?slide=${currentSlide}`);
        }
    }, [currentSlide]);

    const advanceSlide = () => {
        setCurrentSlide(currentSlide + 1);

        navigate(`${location.pathname}?slide=${currentSlide + 1}`);
    };

    const previousSlide = () => {
        setCurrentSlide(currentSlide - 1);

        if (currentSlide - 1 > 0) {
            navigate(`${location.pathname}?slide=${currentSlide - 1}`);
        } else {
            navigate(`${location.pathname}`);
        }
    };

    const saveOnboarding = () => {
        const payload: ISuppliersCompleteOnboardingDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            details: companyDetails,
        };

        props.socket.emit(EndpointsEnum.SuppliersCompleteOnboarding, payload);

        setIsSaving(true);
    };

    const onCompleteOnboarding = (data: IOnSuppliersCompleteOnboardingDTO) => {
        const notification: INotification = {
            message: "successfully_completed_onboarding",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);

        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
        };

        props.socket.emit(EndpointsEnum.AppLoadUser, payload);

        updateSupplierSettings();

        setIsSaving(false);
    };

    const onLoadUser = () => {
        navigate("/dashboard");
    };

    const updateSupplierSettings = () => {
        const payload: IUpdateSupplierSettingsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            settings: {
                theme: {
                    type: companyDetails.theme,
                    allowCustomDark: false,
                    allowCustomLight: false,
                },
            },
        };

        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const updateCompanyDetails = {
            ...companyDetails,
        };

        // @ts-expect-error
        updateCompanyDetails[name] = value;

        setCompanyDetails(updateCompanyDetails);
    };

    const editShipTo = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const updateCompanyDetails = {
            ...companyDetails,
        };

        // @ts-expect-error
        updateCompanyDetails.shipTo[name] = value;

        setCompanyDetails(updateCompanyDetails);
    };

    const editBillTo = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const updateCompanyDetails = {
            ...companyDetails,
        };

        // @ts-expect-error
        updateCompanyDetails.billTo[name] = value;

        setCompanyDetails(updateCompanyDetails);
    };

    const slideOne = () => {
        return (
            <div className={"slide slide-1"}>
                <div>
                    <OwLabel title={"first_lets_confirm_company_details"} />
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel title={"company_name"} />
                        </div>
                        <div>
                            <OwInputWrapper name={"companyName"} fn={handleChange} value={companyDetails.companyName} />
                        </div>
                    </div>

                    <div className={"mt-3"}>
                        <div>
                            <OwLabel title={"company_email"} />
                        </div>
                        <div>
                            <OwInputWrapper name={"companyEmail"} fn={handleChange} value={companyDetails.companyEmail} />
                        </div>
                    </div>

                    <div className={"mt-3"}>
                        <div>
                            <OwLabel title={"ship_to"} />
                        </div>
                        <div>
                            <AddressBlock
                                address={companyDetails.shipTo}
                                fn={editShipTo}
                                showName={true}
                                showCompanyName={true}
                                showPhoneFax={true}
                                hideFax={true}
                            />
                        </div>
                    </div>

                    <div className={"mt-3"}>
                        <div>
                            <OwLabel title={"bill_to"} />
                        </div>
                        <div>
                            <AddressBlock
                                address={companyDetails.billTo}
                                fn={editBillTo}
                                showName={true}
                                showCompanyName={true}
                                showPhoneFax={true}
                                hideFax={true}
                            />
                        </div>
                    </div>
                </div>
                <div className={"mt-3"}>
                    <OwButton title={"next"} fn={advanceSlide} />
                </div>
            </div>
        );
    };

    const selectTheme = (theme: "LIGHT" | "DARK") => {
        const updateCompanyDetails = {
            ...companyDetails,
        };

        updateCompanyDetails.theme = theme;

        setCompanyDetails(updateCompanyDetails);
    };

    const accountNameDesignationComponent = () => {
        const accountNameDesignationOptions = [
            { value: VendorNameOverrideEnum.NULL, title: "Vendor" },
            { value: VendorNameOverrideEnum.RETAILER, title: "Retailer" },
            { value: VendorNameOverrideEnum.CUSTOMER, title: "Customer" },
            { value: VendorNameOverrideEnum.DEALER, title: "Dealer" },
            { value: VendorNameOverrideEnum.RESELLER, title: "Reseller" },
        ];

        const nameOverrideComponent = (
            <div className={"d-flex "}>
                <div className={""}>
                    <OwSelectOption
                        selectedOption={companyDetails}
                        selectedOptionProp={"accountNameDesignation"}
                        options={accountNameDesignationOptions}
                        valueProp={"value"}
                        titleProp={"title"}
                        fn={handleChange}
                        name={"accountNameDesignation"}
                    />
                </div>
            </div>
        );

        return nameOverrideComponent;
    };

    const slideTwo = () => {
        return (
            <div className={"slide slide-2"}>
                <div>
                    <OwLabel title={"now_lets_customize_your_setup"} />
                    <div className={"themeSelection mt-3"}>
                        <div className={"me-2"}>
                            <OwLabel title={"light_mode"} isBold={true} />
                            <div>
                                <OnboardingThemePreview theme={"LIGHT"} />
                            </div>
                            <div className={"text-center"}>
                                <OwInputWrapper
                                    type={"radio"}
                                    name={"theme"}
                                    fn={() => {
                                        selectTheme("LIGHT");
                                    }}
                                    value={companyDetails.theme}
                                />
                            </div>
                        </div>
                        <div>
                            <OwLabel title={"dark_mode"} isBold={true} />
                            <div>
                                <OnboardingThemePreview theme={"DARK"} />
                            </div>
                            <div className={"text-center"}>
                                <OwInputWrapper
                                    type={"radio"}
                                    name={"theme"}
                                    fn={() => {
                                        selectTheme("DARK");
                                    }}
                                    value={companyDetails.theme}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={"businessSettings"}>
                        <div>
                            <OwLabel title={"account_name_designation"} />
                            <WhatsThis subject={"help_text_vendor_name_override"} />
                            {accountNameDesignationComponent()}
                        </div>
                        <div>
                            <OwToggle
                                labelOnRight={true}
                                fn={handleChange}
                                name={"enablePhotoView"}
                                label={"order_entry_photo_view"}
                                active={companyDetails.enablePhotoView}
                            />
                            <WhatsThis subject={"help_text_has_photo_view"} />
                        </div>
                        <div>
                            <OwToggle
                                labelOnRight={true}
                                fn={handleChange}
                                name={"enableShipments"}
                                label={"shipments"}
                                active={companyDetails.enableShipments}
                            />
                            <WhatsThis subject={"help_text_has_shipments"} />
                        </div>
                        <div>
                            <OwToggle
                                labelOnRight={true}
                                fn={handleChange}
                                name={"enableInventory"}
                                label={"inventory"}
                                active={companyDetails.enableInventory}
                            />
                            <WhatsThis subject={"help_text_has_inventory"} />
                        </div>
                        <div>
                            <OwToggle
                                labelOnRight={true}
                                fn={handleChange}
                                name={"enablePickListManagement"}
                                label={"pick_list_management"}
                                active={companyDetails.enablePickListManagement}
                            />
                            <WhatsThis subject={"help_text_pick_list_management"} />
                        </div>
                        <div>
                            <OwToggle
                                labelOnRight={true}
                                fn={handleChange}
                                name={"createDemoData"}
                                label={"create_demo_data"}
                                active={companyDetails.createDemoData}
                            />
                            <WhatsThis subject={"help_text_create_demo_data"} />
                        </div>
                    </div>
                </div>
                <div className={"mt-3"}>
                    <OwButton className={"btn-secondary me-3"} title={"back"} fn={previousSlide} />
                    <OwButton title={"next"} fn={advanceSlide} />
                </div>
            </div>
        );
    };

    const slideThree = () => {
        return (
            <div className={"slide slide-3"}>
                <div>
                    <div className={"mt-3"}>
                        <OwLabel isBold={true} className={"me-3"} title={"company_name"} postText={":"} />
                        {companyDetails.companyName}
                    </div>
                    <div className={"mt-3"}>
                        <OwLabel isBold={true} className={"me-3"} title={"company_email"} postText={":"} />
                        {companyDetails.companyEmail}
                    </div>
                    <div className={"d-flex mt-3"}>
                        <div className={"me-5"}>
                            <div>
                                <OwLabel title={"bill_to"} isBold={true} />
                            </div>
                            <AddressDisplay
                                address={companyDetails.billTo}
                                showCompanyName={true}
                                showName={true}
                                hideEmail={true}
                                showPhone={true}
                                hideFax={true}
                                hideMobile={true}
                            />
                        </div>

                        <div>
                            <div>
                                <OwLabel title={"ship_to"} isBold={true} />
                            </div>
                            <AddressDisplay
                                address={companyDetails.shipTo}
                                showCompanyName={true}
                                showName={true}
                                hideEmail={true}
                                showPhone={true}
                                hideFax={true}
                                hideMobile={true}
                            />
                        </div>
                    </div>
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel className={"me-3"} title={"theme"} isBold={true} postText={":"} />
                            {_.startCase(companyDetails.theme.toLowerCase())}
                        </div>
                    </div>
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel className={"me-3"} title={"account_name_designation"} isBold={true} postText={":"} />
                            {_.startCase(companyDetails.accountNameDesignation || "")}
                        </div>
                    </div>
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel className={"w-12r me-3"} title={"order_entry_photo_view"} isBold={true} postText={":"} />
                            {companyDetails.enablePhotoView ? (
                                <i className={"fad fa-check-circle text-success"} />
                            ) : (
                                <i className={"fad fa-times-circle text-danger"} />
                            )}
                        </div>
                    </div>
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel className={"w-12r me-3"} title={"shipments"} isBold={true} postText={":"} />
                            {companyDetails.enableShipments ? (
                                <i className={"fad fa-check-circle text-success"} />
                            ) : (
                                <i className={"fad fa-times-circle text-danger"} />
                            )}
                        </div>
                    </div>
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel className={"w-12r me-3"} title={"inventory"} isBold={true} postText={":"} />
                            {companyDetails.enableInventory ? (
                                <i className={"fad fa-check-circle text-success"} />
                            ) : (
                                <i className={"fad fa-times-circle text-danger"} />
                            )}
                        </div>
                    </div>
                    <div className={"mt-3"}>
                        <div>
                            <OwLabel className={"w-12r me-3"} title={"pick_list_management"} isBold={true} postText={":"} />
                            {companyDetails.enablePickListManagement ? (
                                <i className={"fad fa-check-circle text-success"} />
                            ) : (
                                <i className={"fad fa-times-circle text-danger"} />
                            )}
                        </div>
                    </div>
                </div>
                <div className={"mt-3"}>
                    <OwButton className={"me-3 btn-secondary"} title={"back"} fn={previousSlide} disabled={isSaving} />
                    <OwButton title={"save"} fn={saveOnboarding} active={isSaving} disableOnActive={true} />
                </div>
            </div>
        );
    };

    return (
        <div className={"Onboarding"}>
            <div className={"mainSection"}>
                <img src={orderwerksLogoDarkMode} height={45} />
                <div>
                    <OwLabel title={"lets_get_your_business_onboarded"} />
                </div>
            </div>

            <div className={"navCircles"}>
                <OnboardingNavCircles numSlides={3} currentSlide={currentSlide} />
            </div>

            <div className={"slides"}>
                {currentSlide === 0 ? slideOne() : null}
                {currentSlide === 1 ? slideTwo() : null}
                {currentSlide === 2 ? slideThree() : null}
            </div>
        </div>
    );
};
